import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiExternalLink, FiArrowRight } = FiIcons;

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('All Projects');

  const portfolioItems = [
    {
      title: 'Spice Route FC Restaurant Chain',
      category: 'Branding',
      subcategory: 'Web Design',
      challenge: 'Create cohesive brand for 5-location Indian restaurant chain',
      solution: 'Warm, authentic brand identity with online ordering system',
      results: '40% increase in online orders, unified brand recognition',
      image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop',
      tags: ['Branding', 'Web Design']
    },
    {
      title: 'Karoo Wind Energy Solutions',
      category: 'Branding',
      subcategory: 'Complete Brand Identity',
      challenge: 'Position renewable energy startup in competitive market',
      solution: 'Clean, modern brand emphasizing sustainability and reliability',
      results: 'Secured R2M in funding, 300% website traffic increase',
      image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=800&h=600&fit=crop',
      tags: ['Branding']
    },
    {
      title: 'Ubuntu Children\'s Excel Foundation',
      category: 'Web Design',
      subcategory: 'Digital Marketing',
      challenge: 'Increase donor engagement and volunteer sign-ups',
      solution: 'Emotional storytelling website with integrated donation platform',
      results: '150% increase in donations, 200+ new volunteers',
      image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=800&h=600&fit=crop',
      tags: ['Web Design', 'Digital Marketing']
    },
    {
      title: 'Stellenbosch 1Mega Wine Estate',
      category: 'Print Design',
      subcategory: 'Digital Design',
      challenge: 'Premium positioning for boutique winery',
      solution: 'Elegant packaging and digital presence reflecting terroir',
      results: '60% increase in direct sales, international distribution deals',
      image: 'https://media.phst.co.za/file/ph/creativesol-portfolio-wine.jpg',
      tags: ['Print Design']
    },
    {
      title: 'TechHub VC Johannesburg',
      category: 'Branding',
      subcategory: 'Web Development',
      challenge: 'Create modern identity for startup incubator',
      solution: 'Dynamic, tech-forward brand with member portal',
      results: '100+ startup applications, major corporate partnerships',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop',
      tags: ['Branding', 'Web Design']
    },
    {
      title: 'Mama Mu Afrika Fashion',
      category: 'Web Design',
      subcategory: 'Branding',
      challenge: 'Launch online store for African-inspired fashion',
      solution: 'Vibrant brand identity with seamless shopping experience',
      results: 'R500K+ online sales in first year',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=600&fit=crop',
      tags: ['Web Design', 'Branding']
    }
  ];

  const filters = ['All Projects', 'Branding', 'Web Design', 'Print Design', 'Digital Marketing'];
  const filteredPortfolio = activeFilter === 'All Projects' ? portfolioItems : portfolioItems.filter(item => item.tags.includes(activeFilter));

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="font-poppins font-bold text-4xl md:text-5xl lg:text-6xl mb-6">
              Our <span className="text-purple-600">Portfolio</span>
            </h1>
            <p className="font-inter text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
              Explore our collection of successful projects that have transformed businesses across South Africa and helped them achieve remarkable results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-6 py-3 rounded-full font-inter font-medium transition-all duration-300 ${activeFilter === filter
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-gray-50 text-gray-600 hover:bg-purple-600 hover:text-white'
                  }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {filteredPortfolio.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl overflow-hidden shadow-xl card-hover"
              >
                {/* Project Image */}
                <div className="relative group">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gray-900 bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                    <SafeIcon icon={FiExternalLink} className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                  <div className="absolute top-4 left-4">
                    <div className="flex flex-wrap gap-2">
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 bg-purple-600 text-white text-sm font-inter font-medium rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Project Details */}
                <div className="p-8">
                  <h3 className="font-poppins font-bold text-2xl text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="font-inter text-purple-600 font-medium mb-6">
                    {item.category} + {item.subcategory}
                  </p>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-poppins font-semibold text-gray-900 mb-1">
                        Challenge:
                      </h4>
                      <p className="font-inter text-gray-600 text-sm">
                        {item.challenge}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-poppins font-semibold text-gray-900 mb-1">
                        Solution:
                      </h4>
                      <p className="font-inter text-gray-600 text-sm">
                        {item.solution}
                      </p>
                    </div>
                    <div>
                      <h4 className="font-poppins font-semibold text-gray-900 mb-1">
                        Results:
                      </h4>
                      <p className="font-inter text-gray-600 text-sm">
                        {item.results}
                      </p>
                    </div>
                  </div>
                  <button className="mt-6 inline-flex items-center text-purple-600 font-inter font-medium hover:text-teal-600 transition-colors duration-200">
                    View Case Study <SafeIcon icon={FiArrowRight} className="ml-2 w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
              Ready to Join Our Success Stories?
            </h2>
            <p className="font-inter text-lg text-gray-300 max-w-2xl mx-auto mb-8">
              Let's create something amazing together. Contact us to discuss your project and see how we can help transform your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center px-8 py-4 bg-purple-600 text-white font-poppins font-semibold rounded-lg hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105"
              >
                Start Your Project <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5" />
              </a>
              <a
                href="/process"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-poppins font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300"
              >
                Learn Our Process
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;